import { useEffect, useRef } from "react";

/**
 * Reproductor de audio de una nota (servicio propio en tts.waia.ar).
 *
 * El script del servicio no expone una API: al cargar busca los
 * `[data-audio-widget]` que hay en el documento UNA vez, y si la nota no tiene
 * audio listo (404, "pending", "processing") ELIMINA el contenedor del DOM.
 * Dos consecuencias para una SPA con React:
 *
 * 1. El contenedor lo crea este efecto con el DOM directo y no el JSX. Si lo
 *    renderizara React y el script lo quitara, al desmontar la nota React
 *    intentaria sacar un nodo que ya no esta y tiraria NotFoundError.
 * 2. El script se inyecta de nuevo en cada nota, despues de crear el
 *    contenedor: sin un observer propio, es la unica forma de que escanee el
 *    DOM nuevo al navegar de una nota a otra sin recargar. Es idempotente: no
 *    deja globales y sus estilos tienen id propio.
 *
 * Se inyecta en idle y solo en las notas, asi no suma peso al resto del sitio
 * ni compite con el render inicial. El audio de cada nota lo carga
 * scripts/audio-sync.mjs.
 */

const SCRIPT_SRC = "https://tts.waia.ar/build/embed/audio-widget.js";
// La API de tts.waia.ar no manda CORS para este sitio, asi que el widget le
// habla a /tts-api/ (proxy de nginx.conf y de vite.config.ts), same-origin. Si
// el servicio habilita CORS: API_BASE = "https://tts.waia.ar" y se borra el
// proxy.
const API_PATH = "/tts-api";
// Identificador PUBLICO de la cuenta: viaja en el HTML de cada pagina por
// diseno del servicio. El secreto del webhook NO va aca (solo en el script de
// sincronizacion, por variable de entorno).
const ACCOUNT_UUID = "E6emyMdVpLqhrSDEB2";

/** Si en este tiempo no aparecio el reproductor, se libera el espacio reservado. */
const GIVE_UP_MS = 8000;

type State = "pending" | "ready" | "empty";

interface AudioWidgetProps {
  /** Id con el que la nota se cargo en el servicio: el slug. */
  articleId: string;
  theme?: "light" | "dark" | "auto";
}

function AudioWidget({ articleId, theme = "light" }: AudioWidgetProps) {
  const holderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;

    const setState = (state: State) => {
      holder.dataset.state = state;
    };

    const container = document.createElement("div");
    container.setAttribute("data-audio-widget", "");
    container.dataset.articleId = articleId;
    container.dataset.theme = theme;
    holder.replaceChildren(container);
    setState("pending");

    // El script quita el contenedor cuando no hay audio: el hueco se cierra.
    const observer = new MutationObserver(() => {
      if (!holder.firstElementChild) {
        setState("empty");
        observer.disconnect();
      } else if (container.classList.contains("aw-widget--ready")) {
        setState("ready");
      }
    });
    observer.observe(holder, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    let script: HTMLScriptElement | null = null;
    const inject = () => {
      script = document.createElement("script");
      script.async = true;
      script.src = SCRIPT_SRC;
      script.dataset.uuid = ACCOUNT_UUID;
      script.dataset.apiBase = `${window.location.origin}${API_PATH}`;
      script.dataset.theme = theme;
      // Bloqueado (CSP, extension) o sin red: no dejar el hueco reservado.
      script.onerror = () => setState("empty");
      document.body.appendChild(script);
    };

    // Los cancel van envueltos: sacados de window y llamados como metodo de
    // otro objeto, el navegador responde "Illegal invocation".
    const idle =
      typeof window.requestIdleCallback === "function"
        ? {
            id: window.requestIdleCallback(inject, { timeout: 2000 }),
            cancel: (id: number) => window.cancelIdleCallback(id),
          }
        : {
            id: window.setTimeout(inject, 600),
            cancel: (id: number) => window.clearTimeout(id),
          };

    const giveUp = window.setTimeout(() => {
      if (holder.dataset.state === "pending") setState("empty");
    }, GIVE_UP_MS);

    return () => {
      idle.cancel(idle.id);
      window.clearTimeout(giveUp);
      observer.disconnect();
      script?.remove();
      holder.replaceChildren();
    };
  }, [articleId, theme]);

  return <div ref={holderRef} className="post-audio" data-state="pending" />;
}

export default AudioWidget;
